import { Component, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { HttpParams } from '@angular/common/http'

import { BaseComponent } from '../../../shared'
import { PlanComment } from '../../../shared/data-models/plan-comment'

declare var mermaid: any

const PLAN_KEY = 'procure-to-pay'

interface DiagramDef {
    id: string
    processName: string
    code: string
}

@Component({
    selector: 'app-procure-to-pay',
    templateUrl: './procure-to-pay.component.html',
    styleUrls: ['./procure-to-pay.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class ProcureToPayComponent extends BaseComponent implements AfterViewInit, OnDestroy {
    @ViewChild('diagramPo') diagramPoEl!: ElementRef
    @ViewChild('diagramRecv') diagramRecvEl!: ElementRef
    @ViewChild('diagramInv') diagramInvEl!: ElementRef
    @ViewChild('diagramCombined') diagramCombinedEl!: ElementRef

    comments: PlanComment[] = []
    selectedNodeId: string | null = null
    selectedNodeLabel = ''
    selectedNodeProcess = ''
    sidebarOpen = false
    newCommentText = ''

    private nodeProcessMap: Record<string, string> = {}
    private mermaidLoaded = false

    diagrams: DiagramDef[] = [
        {
            id: 'diagram-po',
            processName: 'Purchase Orders',
            code: `flowchart LR
    A["User Opens\\nPurchasing View"] --> B{"Role / Job\\nDetection"}
    B --> C["Adaptive\\nInput Form"]
    C --> D["Enter Items\\nVendor, Qty, Price"]
    D --> E{"PO Type?"}
    E -->|Standard| F{"Approval\\nRequired?"}
    E -->|"Point of Sale"| G["Auto-Generate\\nPO Number"]
    E -->|Subcontract| F
    F -->|Yes| H["Route to\\nApprover"]
    F -->|No| I["Auto-Approve"]
    H --> J{"Decision"}
    J -->|Approve| I
    J -->|Reject| K["Return to User"]
    J -->|Modify| C
    I --> L["Generate\\nPO Number"]
    G --> L
    L --> M["Push to\\nVista API"]
    M --> N["PO Active\\nin Vista"]

    style A fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style M fill:#3498db,color:#fff,stroke:#3498db
    style N fill:#1abc9c,color:#fff,stroke:#1abc9c
    style F fill:#d35400,color:#fff,stroke:#d35400
    style J fill:#d35400,color:#fff,stroke:#d35400
    style E fill:#d35400,color:#fff,stroke:#d35400
    style B fill:#8e44ad,color:#fff,stroke:#8e44ad`,
        },
        {
            id: 'diagram-recv',
            processName: 'Receiving',
            code: `flowchart LR
    A2["Delivery\\nArrives"] --> B2["Tagged Receiver\\nNotified"]
    B2 --> C2["Open PO\\non Mobile"]
    C2 --> D2["Photo/Scan\\nPacking Slip"]
    D2 --> E2["AI Reads\\nSlip Data"]
    E2 --> F2["Pre-fill\\nReceived Items"]
    F2 --> G2{"All Items\\nReceived?"}
    G2 -->|Full| H2["Mark PO\\nFully Received"]
    G2 -->|Partial| I2["Record Partial\\nUpdate Balance"]
    G2 -->|"Missing Items"| J2["Flag for\\nVendor Follow-up"]
    I2 --> K2["Await Next\\nDelivery"]
    K2 --> A2
    H2 --> L2["Review\\nQueue"]
    I2 --> L2
    L2 --> M2{"Review\\nPassed?"}
    M2 -->|Yes| N2["Push to\\nVista API"]
    M2 -->|No| O2["Return for\\nCorrection"]
    N2 --> P2["Receiving Record\\nin Vista"]

    style A2 fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style E2 fill:#8e44ad,color:#fff,stroke:#8e44ad
    style G2 fill:#d35400,color:#fff,stroke:#d35400
    style M2 fill:#d35400,color:#fff,stroke:#d35400
    style N2 fill:#3498db,color:#fff,stroke:#3498db
    style P2 fill:#1abc9c,color:#fff,stroke:#1abc9c`,
        },
        {
            id: 'diagram-inv',
            processName: 'Invoicing',
            code: `flowchart LR
    A3["Vendor Sends\\nInvoice Email"] --> B3["Graph API\\nReads Mailbox"]
    B3 --> C3["Extract\\nAttachments"]
    C3 --> D3["Anthropic AI\\nProcesses PDF"]
    D3 --> E3["Extract Header\\n+ Line Items"]
    E3 --> F3["Assign\\nConfidence Scores"]
    F3 --> G3["Match to\\nExisting PO"]
    G3 --> H3["Review\\nDashboard"]
    H3 --> I3["Split View:\\nDoc and Data"]
    I3 --> J3["Reviewer Verifies\\nAdds Phase Codes"]
    J3 --> K3{"Approved?"}
    K3 -->|Yes| L3["Stage for\\nUpload"]
    K3 -->|No| M3["Return for\\nCorrection"]
    L3 --> N3["Push to\\nVista API"]
    N3 --> O3["Invoice in Vista\\nfor Payment"]

    style A3 fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style B3 fill:#3498db,color:#fff,stroke:#3498db
    style D3 fill:#8e44ad,color:#fff,stroke:#8e44ad
    style F3 fill:#8e44ad,color:#fff,stroke:#8e44ad
    style K3 fill:#d35400,color:#fff,stroke:#d35400
    style N3 fill:#3498db,color:#fff,stroke:#3498db
    style O3 fill:#1abc9c,color:#fff,stroke:#1abc9c`,
        },
    ]

    combinedDiagram: DiagramDef = {
        id: 'diagram-combined',
        processName: 'Combined Overview',
        code: `flowchart TB
    subgraph PO ["Phase 1: Purchase Orders"]
        direction LR
        A1o["User Opens\\nPurchasing"] --> B1o{"Role/Job\\nDetect"}
        B1o --> C1o["Adaptive Form"]
        C1o --> D1o["Enter Items"]
        D1o --> E1o{"PO Type?"}
        E1o --> F1o{"Approval?"}
        F1o -->|Yes| G1o["Route to\\nApprover"]
        F1o -->|No| H1o["Auto-Approve"]
        G1o --> I1o{"Decision"}
        I1o -->|Approve| H1o
        H1o --> J1o["Generate PO#"]
        J1o --> K1o["Push to Vista"]
        K1o --> L1o["PO Active\\nin Vista"]
    end

    subgraph RECV ["Phase 2: Receiving"]
        direction LR
        A2o["Delivery\\nArrives"] --> B2o["Receiver\\nNotified"]
        B2o --> C2o["Open PO\\nMobile"]
        C2o --> D2o["Scan Slip"]
        D2o --> E2o["AI Reads\\nSlip"]
        E2o --> F2o{"All Received?"}
        F2o -->|Full| G2o["Fully\\nReceived"]
        F2o -->|Partial| H2o["Record\\nPartial"]
        G2o --> I2o["Review"]
        H2o --> I2o
        I2o --> J2o["Push to Vista"]
        J2o --> K2o["Receipt\\nin Vista"]
    end

    subgraph INV ["Phase 3: Invoicing"]
        direction LR
        A3o["Vendor Email"] --> B3o["Graph API"]
        B3o --> C3o["Extract\\nAttachments"]
        C3o --> D3o["AI Extracts\\nData"]
        D3o --> E3o["Confidence\\nScores"]
        E3o --> F3o["Match to PO\\nand Receipt"]
        F3o --> G3o["Human\\nReview"]
        G3o --> H3o{"Approved?"}
        H3o -->|Yes| I3o["Stage"]
        I3o --> J3o["Push to Vista"]
        J3o --> K3o["Invoice in Vista\\nfor Payment"]
    end

    L1o -->|"PO issued, delivery expected"| A2o
    K2o -->|"Receipt data for matching"| F3o
    L1o -->|"PO data for 3-way match"| F3o

    style L1o fill:#1abc9c,color:#fff,stroke:#1abc9c
    style K2o fill:#1abc9c,color:#fff,stroke:#1abc9c
    style K3o fill:#1abc9c,color:#fff,stroke:#1abc9c
    style A1o fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style A2o fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style A3o fill:#e8a838,color:#1a1a2e,stroke:#e8a838
    style E2o fill:#8e44ad,color:#fff,stroke:#8e44ad
    style D3o fill:#8e44ad,color:#fff,stroke:#8e44ad`,
    }

    get selectedNodeComments(): PlanComment[] {
        if (!this.selectedNodeId) return []
        return this.comments.filter(c => c.nodeId === this.selectedNodeId)
    }

    get totalCommentCount(): number {
        return this.comments.length
    }

    private get commentCountsByNodeId(): Record<string, number> {
        const counts: Record<string, number> = {}
        for (const c of this.comments) {
            counts[c.nodeId] = (counts[c.nodeId] || 0) + 1
        }
        return counts
    }

    constructor() {
        super()
        this.isInitialLoading = false
    }

    ngAfterViewInit() {
        this.loadMermaid().then(() => {
            this.renderDiagrams()
            this.loadComments()
        })
    }

    override ngOnDestroy() {
        super.ngOnDestroy()
    }

    private loadMermaid(): Promise<void> {
        if (this.mermaidLoaded || typeof mermaid !== 'undefined') {
            this.mermaidLoaded = true
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            const script = document.createElement('script')
            script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js'
            script.onload = () => {
                this.mermaidLoaded = true
                resolve()
            }
            document.head.appendChild(script)
        })
    }

    private async renderDiagrams() {
        mermaid.initialize({
            startOnLoad: false,
            theme: 'dark',
            themeVariables: {
                primaryColor: '#e8a838',
                primaryTextColor: '#1a1a2e',
                primaryBorderColor: '#e8a838',
                lineColor: '#bdc3c7',
                secondaryColor: '#16213e',
                tertiaryColor: '#0f3460',
                fontFamily: 'inherit',
                fontSize: '13px',
            },
            flowchart: {
                htmlLabels: true,
                curve: 'basis',
                nodeSpacing: 30,
                rankSpacing: 40,
            },
        })

        const allDiagrams = [...this.diagrams, this.combinedDiagram]
        for (const diag of allDiagrams) {
            const el = document.getElementById(diag.id)
            if (!el) continue
            const { svg } = await mermaid.render(diag.id + '-svg', diag.code)
            el.innerHTML = svg
        }

        setTimeout(() => this.wireNodeClicks(), 500)
    }

    private wireNodeClicks() {
        for (const diag of this.diagrams) {
            const container = document.getElementById(diag.id)
            if (!container) continue
            container.querySelectorAll('.node').forEach((node: Element) => {
                this.nodeProcessMap[node.id] = diag.processName
                ;(node as HTMLElement).style.cursor = 'pointer'
                node.addEventListener('click', (e) => {
                    e.stopPropagation()
                    const label = this.getNodeLabel(node)
                    this.selectNode(node.id, label, diag.processName)
                })
            })
        }

        const combinedContainer = document.getElementById(this.combinedDiagram.id)
        if (combinedContainer) {
            combinedContainer.querySelectorAll('.node').forEach((node: Element) => {
                this.nodeProcessMap[node.id] = this.combinedDiagram.processName
                ;(node as HTMLElement).style.cursor = 'pointer'
                node.addEventListener('click', (e) => {
                    e.stopPropagation()
                    const label = this.getNodeLabel(node)
                    this.selectNode(node.id, label, this.combinedDiagram.processName)
                })
            })
        }
    }

    private getNodeLabel(node: Element): string {
        const labelEl = node.querySelector('.nodeLabel') || node.querySelector('foreignObject span') || node.querySelector('text')
        return labelEl?.textContent?.trim().replace(/\n/g, ' ') || node.id
    }

    private updateNodeBadges() {
        const counts = this.commentCountsByNodeId
        const allContainers = [...this.diagrams, this.combinedDiagram]
            .map(d => document.getElementById(d.id))
            .filter(Boolean) as HTMLElement[]

        for (const container of allContainers) {
            container.querySelectorAll('.node').forEach((node: Element) => {
                const existing = node.querySelector('.comment-badge-group')
                if (existing) existing.remove()

                const count = counts[node.id]
                if (!count) return

                const bbox = (node as SVGGraphicsElement).getBBox?.()
                if (!bbox) return

                const ns = 'http://www.w3.org/2000/svg'
                const g = document.createElementNS(ns, 'g')
                g.setAttribute('class', 'comment-badge-group')

                const label = count > 9 ? '9+' : String(count)
                const isWide = label.length > 1
                const r = 14
                const pillWidth = isWide ? 32 : 28
                const pillHeight = 28
                const cx = bbox.x + bbox.width + 4
                const cy = bbox.y - 4

                const rect = document.createElementNS(ns, 'rect')
                rect.setAttribute('x', String(cx - pillWidth / 2))
                rect.setAttribute('y', String(cy - pillHeight / 2))
                rect.setAttribute('width', String(pillWidth))
                rect.setAttribute('height', String(pillHeight))
                rect.setAttribute('rx', String(r))
                rect.setAttribute('ry', String(r))
                rect.setAttribute('fill', '#e74c3c')
                rect.setAttribute('stroke', '#ffffff')
                rect.setAttribute('stroke-width', '2.5')
                rect.setAttribute('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))')

                const text = document.createElementNS(ns, 'text')
                text.setAttribute('x', String(cx))
                text.setAttribute('y', String(cy + 1))
                text.setAttribute('text-anchor', 'middle')
                text.setAttribute('dominant-baseline', 'central')
                text.setAttribute('fill', '#ffffff')
                text.setAttribute('font-size', '14')
                text.setAttribute('font-weight', '800')
                text.setAttribute('font-family', 'Arial, Helvetica, sans-serif')
                text.textContent = label

                g.appendChild(rect)
                g.appendChild(text)
                node.appendChild(g)
            })
        }
    }

    selectNode(nodeId: string, nodeLabel: string, processName: string) {
        this.selectedNodeId = nodeId
        this.selectedNodeLabel = nodeLabel
        this.selectedNodeProcess = processName
        this.sidebarOpen = true
        this.newCommentText = ''
    }

    toggleSidebar() {
        this.sidebarOpen = !this.sidebarOpen
    }

    closeSidebar() {
        this.sidebarOpen = false
    }

    loadComments() {
        const params = new HttpParams().set('planKey', PLAN_KEY)
        this.services.data.get<PlanComment[]>('api/PlanComment', params).subscribe({
            next: (data) => {
                this.comments = data
                this.updateNodeBadges()
            },
            error: () => {
                this.services.notification.error('Failed to load comments')
            },
        })
    }

    onCommentKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            this.addComment()
        }
    }

    addComment() {
        const text = this.newCommentText.trim()
        if (!text || !this.selectedNodeId) return

        const comment = new PlanComment()
        comment.planKey = PLAN_KEY
        comment.nodeId = this.selectedNodeId
        comment.nodeLabel = this.selectedNodeLabel
        comment.processName = this.selectedNodeProcess
        comment.comment = text

        this.services.data.post<PlanComment>('api/PlanComment', comment).subscribe({
            next: (saved) => {
                this.comments.unshift(saved)
                this.newCommentText = ''
                this.services.notification.success('Comment added')
                this.updateNodeBadges()
            },
            error: () => {
                this.services.notification.error('Failed to save comment')
            },
        })
    }

    deleteComment(comment: PlanComment) {
        if (!confirm('Delete this comment?')) return
        this.services.data.http.delete(`api/PlanComment/${comment.id}`).subscribe({
            next: () => {
                this.comments = this.comments.filter(c => c.id !== comment.id)
                this.services.notification.success('Comment deleted')
                this.updateNodeBadges()
            },
            error: () => {
                this.services.notification.error('Failed to delete comment')
            },
        })
    }
}
